import {
  AppBar,
  Avatar,
  alpha,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Typography,
} from '@mui/material'
import PopupState, { bindPopover, bindTrigger } from 'material-ui-popup-state'
import { useCallback, useContext, useMemo, useState } from 'react'
import { IoMenuSharp, IoReorderThreeOutline } from 'react-icons/io5'
import { Link } from 'react-router-dom'
import { AuthContext } from 'src/components/providers/AuthProvider'
import { baseURL, serviceName } from 'src/config'
import { useAuth } from 'src/hooks/useAuth'
import { ClientContext } from '../providers/ClientProvider'

type Props = {
  onOpenDrawer: () => void
}

export const Header = ({ onOpenDrawer }: Props) => {
  const { logout } = useAuth()
  const [user, isLoading] = useContext(AuthContext)
  const { client, clientMembers } = useContext(ClientContext)
  const [openDrawer, setOpenDrawer] = useState<boolean>(false)

  const currentMember = useMemo(() => {
    return clientMembers.find((m) => m.id === user?.uid)
  }, [clientMembers, user])

  const handleLogout = useCallback(() => {
    logout()
    window.location.reload()
  }, [logout])

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.85),
        backdropFilter: 'saturate(180%) blur(12px)',
        WebkitBackdropFilter: 'saturate(180%) blur(12px)',
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1,
          px: 2,
        }}
      >
        <Box>
          <Link to={client ? `/clients/${client.id}/devices` : '/lp'} style={{ textDecoration: 'none', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <img src="/logomark-color.svg" alt="logo" height="34" />
            <Typography
              sx={{
                color: 'primary.main',
                fontWeight: 800,
                fontSize: { xs: 18, sm: 20 },
                letterSpacing: '0.2px',
                lineHeight: 1,
              }}
            >
              {serviceName}
            </Typography>
          </Link>
        </Box>
        {isLoading && (
          <Box>
            <Button disabled variant="text" size="small">
              ...
            </Button>
          </Box>
        )}
        {!isLoading && user && currentMember && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <PopupState variant="popover" popupId="account-pop">
              {(popupState) => (
                <>
                  <IconButton {...bindTrigger(popupState)} sx={{ p: 0.5 }}>
                    <Avatar src={currentMember.iconURL ?? `${baseURL}/user.png`} sx={{ width: 36, height: 36 }} />
                  </IconButton>
                  <Popover
                    {...bindPopover(popupState)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{
                      paper: { sx: { mt: 1, minWidth: 240, borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 6 } },
                    }}
                  >
                    <Stack>
                      {currentMember.name && (
                        <Box sx={{ px: 2, py: 1.5 }}>
                          <Link to={`/clients/${client?.id}/profile`} onClick={popupState.close} style={{ textDecoration: 'none' }}>
                            <Typography sx={{ fontWeight: 700, color: 'text.primary' }}>{currentMember.name}</Typography>
                          </Link>
                          {user.email && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                              {user.email}
                            </Typography>
                          )}
                        </Box>
                      )}
                      <Divider />
                      <Button onClick={handleLogout} sx={{ borderRadius: 0, justifyContent: 'flex-start', px: 2, py: 1.25, color: 'text.primary' }}>
                        sign out
                      </Button>
                    </Stack>
                  </Popover>
                </>
              )}
            </PopupState>
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <IconButton onClick={onOpenDrawer}>
                <IoReorderThreeOutline />
              </IconButton>
            </Box>
          </Box>
        )}
        {!isLoading && !currentMember && (
          <>
            <Stack direction="row" spacing={1.5} sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
              <Button component={Link} variant="text" to="/auth/login" sx={{ fontWeight: 700, color: 'text.primary' }}>
                ログイン
              </Button>
              <Button
                component={Link}
                variant="contained"
                to="/inquiry/register"
                sx={{ borderRadius: 999, px: 2.5, fontWeight: 700, boxShadow: 'none', '&:hover': { boxShadow: 2 } }}
              >
                ご利用申請
              </Button>
            </Stack>
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
              <IconButton onClick={() => setOpenDrawer((v) => !v)}>
                <IoMenuSharp />
              </IconButton>
            </Box>
            <Drawer
              open={openDrawer}
              onClose={() => setOpenDrawer(false)}
              anchor="right"
              disableScrollLock
              sx={{ display: { xs: 'block', sm: 'none' } }}
              slotProps={{
                paper: { sx: { width: 260 } },
              }}
            >
              <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <img src="/logomark-color.svg" alt="logo" height="28" />
                <Typography sx={{ color: 'primary.main', fontWeight: 800, fontSize: 18 }}>{serviceName}</Typography>
              </Box>
              <Divider />
              <List sx={{ pt: 1 }}>
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/inquiry/register" onClick={() => setOpenDrawer(false)}>
                    <ListItemText
                      primary="ご利用申請"
                      slotProps={{
                        primary: { sx: { fontWeight: 700 } },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component="a" href="/auth/login">
                    <ListItemText
                      primary="ログイン"
                      slotProps={{
                        primary: { sx: { fontWeight: 700 } },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </List>
            </Drawer>
          </>
        )}
      </Box>
    </AppBar>
  )
}
